// A detail screen's agreements card: AgreementsBlock plus the fetching and the archive
// toggle it needs when it stands on its own.
//
// Used by BOTH detail screens — the retainer's, where the contract is the one the CLIENT
// signed for the engagement, and the job's, where it is typically the one a SUPPLIER
// signed for that piece of work (the photographer). `ownerKind` picks the endpoint set;
// nothing else differs, because nothing else about storing a signed contract does.
//
// The client card does NOT use this — it nests AgreementsBlock inside each fold and feeds
// it from one client-wide read, with a single archive toggle at the top of the tab
// covering the whole tree. Here there is exactly one owner on screen, so the toggle
// belongs to this card and the fetch is its own.
import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { agreementApis } from '../api';
import he from '../i18n/he';
import { Loader, ErrorText } from './Loader';
import Button from './Button';
import AgreementsBlock from './AgreementsBlock';

export default function AgreementsSection({ ownerKind = 'retainer', ownerId }) {
  const api = agreementApis[ownerKind];
  const [archived, setArchived] = useState(false);
  const { data, loading, error, reload } = useAsync(
    () => api.list(ownerId, { archived }), [ownerKind, ownerId, archived]
  );

  return (
    <div className="card w-section">
      <AgreementsBlock
        agreements={data || []}
        archived={archived}
        ownerId={ownerId}
        api={api}
        onChange={reload}
        // The heading names what the contract IS for this owner. On a job it is a
        // supplier's agreement, and calling it the same thing as the engagement's
        // contract would be the one place the two could be confused.
        heading={ownerKind === 'job' ? he.agreements.jobPanelTitle : he.agreements.panelTitle}
        // The empty state and the name example name the owner too, and on a job they said
        // "this retainer" — copied wholesale from the retainer's list and never adjusted.
        empty={ownerKind === 'job' ? he.empty.jobAgreements : he.empty.agreements}
        namePlaceholder={ownerKind === 'job'
          ? he.agreements.jobNamePlaceholder
          : he.agreements.namePlaceholder}
        // The status stands in for the LIST only, so switching to the archive never
        // blanks the toggle that was just clicked.
        status={loading ? <Loader /> : (error ? <ErrorText error={error} /> : null)}
        headerActions={
          <Button small variant="subtle" onClick={() => setArchived((a) => !a)}>
            {archived ? he.agreements.showLive : he.agreements.showArchive}
          </Button>
        }
      />
    </div>
  );
}
